import { z } from 'zod';

/** The choice belongs to a document, never to the configuration of a series. */
export const documentNumberingModeSchema = z.enum(['automatic', 'manual']);
export type DocumentNumberingMode = z.infer<typeof documentNumberingModeSchema>;

export const manualDocumentNumberSchema = z.string().trim().min(1, 'Inserisci il numero manuale.')
  .max(50, 'Il numero documento non può superare 50 caratteri.')
  .refine(value => !/[\u0000-\u001f\u007f-\u009f]/.test(value), 'Il numero documento contiene caratteri non ammessi.');

export const documentCreationOperationIdSchema = z.string().uuid()
  .transform(value => value.toLowerCase());

/** Optional fields preserve legacy automatic callers and partial draft updates. */
export const documentNumberingInputFields = {
  numbering_mode: documentNumberingModeSchema.optional(),
  manual_number: manualDocumentNumberSchema.nullish(),
  creation_operation_id: documentCreationOperationIdSchema.optional(),
};

const numberingObjectSchema = z.object(documentNumberingInputFields);
export type DocumentNumberingInput = z.infer<typeof numberingObjectSchema>;

export function refineDocumentNumbering(input: DocumentNumberingInput, ctx: z.RefinementCtx): void {
  if (input.numbering_mode === 'manual' && !input.manual_number) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['manual_number'], message: 'Inserisci il numero manuale.' });
  }
  if (input.manual_number && input.numbering_mode !== 'manual') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['numbering_mode'],
      message: 'Attiva Numero manuale per indicare un numero personalizzato.',
    });
  }
}

export const documentNumberingInputSchema = numberingObjectSchema.superRefine(refineDocumentNumbering);
/**
 * Contratto FE↔BE per il 409 su numero documento duplicato (indice unico
 * `(tenant_id, ddt_number)` di DDT passivi e attivi). Il BE mappa la unique
 * violation su questo codice; la UI mostra il messaggio sul campo indicato.
 */
export const DUPLICATE_DDT_NUMBER_ERROR_CODE = 'DUPLICATE_DDT_NUMBER';
export const DUPLICATE_DDT_NUMBER_MESSAGE = 'Numero DDT già presente per questo tenant';
export const DUPLICATE_DDT_NUMBER_FIELD = 'ddt_number';

export interface DuplicateDdtNumberErrorDetails {
  field: typeof DUPLICATE_DDT_NUMBER_FIELD;
  /** Documento colpito: passivo (fornitore) o attivo (emesso). */
  document: 'ddt_passivo' | 'ddt_attivo';
}

/** Indici unici PostgreSQL che esprimono l'unicità del numero DDT per tenant. */
export const DUPLICATE_DDT_NUMBER_CONSTRAINTS: Readonly<Record<string, DuplicateDdtNumberErrorDetails['document']>> = {
  idx_pos_ddtp_number: 'ddt_passivo',
  idx_pos_ddt_number: 'ddt_attivo',
};

/**
 * Riconosce il payload d'errore `{ error: { code, details } }` del 409 numero
 * DDT duplicato a partire da un body sconosciuto (client HTTP tipizzato).
 */
export function isDuplicateDdtNumberErrorPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object' || !('error' in payload)) return false;
  const error: unknown = payload.error;
  if (!error || typeof error !== 'object' || !('code' in error)) return false;
  return error.code === DUPLICATE_DDT_NUMBER_ERROR_CODE;
}
